import { create } from 'zustand';
import { supabase, DEFAULT_HOUSE_ID, currentMonthYear } from '../lib/supabase';
import type {
  Card, Invoice, RecurringExpense, RecurringOccurrence,
  Income, Installment, InstallmentOccurrence, House,
  Participant, CommitmentItem, MonthSummary,
  OneOffExpense, RecurringIncome, MonthHistory,
  Account, Budget, Goal, CategoryId, ReportData,
} from '../types';
import { normalizeCategory } from '../types';
import { runFinanceCommand } from '../lib/financeCommand';
import { getDaysInMonth } from 'date-fns';
import { clearFinanceCache, readFinanceCache, writeFinanceCache, type CachedMonthData, type FinanceCacheSnapshot } from '../lib/offlineCache';
import { categoryTotalsForItems, commitmentItemsFromRows, getPeriodMonths, historyFromRows, isPeriodRow, launchItemsFromRows } from './financeSelectors';

interface AppState {
  // Current month context
  selectedMonth: number;
  selectedYear: number;

  // Data
  house: House | null;
  participants: Participant[];
  cards: Card[];
  invoices: Invoice[];
  recurringExpenses: RecurringExpense[];
  recurringOccurrences: RecurringOccurrence[];
  recurringIncomes: RecurringIncome[];
  incomes: Income[];
  installments: Installment[];
  installmentOccurrences: InstallmentOccurrence[];
  oneOffExpenses: OneOffExpense[];
  accounts: Account[];
  budgets: Budget[];
  goals: Goal[];

  // UI
  loading: boolean;
  error: string | null;
  isOffline: boolean;
  lastSyncedAt: string | null;

  // Actions
  setMonth: (month: number, year: number) => void;
  fetchAll: () => Promise<void>;
  fetchMonthData: (month: number, year: number) => Promise<void>;
  fetchMonthHistory: () => Promise<MonthHistory[]>;
  fetchReportData: (months?: number) => Promise<ReportData>;
  addIncome: (data: Partial<Income>) => Promise<void>;
  updateIncome: (id: string, patch: { name?: string; amount?: number; date?: string }) => Promise<void>;
  deleteIncome: (id: string) => Promise<void>;
  upsertInvoice: (data: Partial<Invoice>) => Promise<void>;
  updateInvoice: (id: string, data: { amount: number }) => Promise<void>;
  deleteInvoice: (id: string) => Promise<void>;
  payInvoice: (invoiceId: string) => Promise<void>;
  unpayInvoice: (invoiceId: string) => Promise<void>;
  payOccurrence: (occurrenceId: string) => Promise<void>;
  unpayOccurrence: (occurrenceId: string) => Promise<void>;
  payInstallment: (occurrenceId: string) => Promise<void>;
  unpayInstallment: (occurrenceId: string) => Promise<void>;
  updateOccurrenceAmount: (occurrenceId: string, amount: number) => Promise<void>;
  updateInstallment: (id: string, data: { installment_amount: number; installment_count: number }) => Promise<void>;
  deleteInstallment: (id: string) => Promise<void>;
  addCard: (data: Partial<Card>) => Promise<void>;
  updateCard: (id: string, data: { name?: string; last_digits?: string | null; due_day?: number | null }) => Promise<void>;
  deleteCard: (id: string) => Promise<void>;
  addRecurring: (data: Partial<RecurringExpense>) => Promise<void>;
  updateRecurring: (id: string, data: Partial<RecurringExpense>) => Promise<void>;
  deleteRecurring: (id: string) => Promise<void>;
  deleteRecurringOccurrence: (id: string) => Promise<void>;
  addRecurringIncome: (data: Partial<RecurringIncome>) => Promise<void>;
  deleteRecurringIncome: (id: string) => Promise<void>;
  addOneOff: (data: Partial<OneOffExpense>) => Promise<string | undefined>;
  updateOneOff: (id: string, patch: { name?: string; amount?: number; date?: string; category?: string }) => Promise<void>;
  deleteOneOff: (id: string) => Promise<void>;
  payOneOff: (id: string) => Promise<void>;
  unpayOneOff: (id: string) => Promise<void>;
  generateOccurrences: (month: number, year: number) => Promise<void>;
  addInstallment: (data: { card_id: string; name: string; amount: number; total_installments: number; start_month?: number; start_year?: number; category?: CategoryId }) => Promise<void>;
  fetchAccounts: () => Promise<void>;
  addAccount: (data: Partial<Account>) => Promise<void>;
  updateAccountBalance: (id: string, balance: number) => Promise<void>;
  deleteAccount: (id: string) => Promise<void>;
  fetchBudgets: (month: number, year: number) => Promise<void>;
  saveBudget: (category: CategoryId, amount: number, month: number, year: number) => Promise<void>;
  deleteBudget: (id: string) => Promise<void>;
  fetchGoals: () => Promise<void>;
  addGoal: (data: Partial<Goal>) => Promise<void>;
  updateGoalProgress: (id: string, amount: number) => Promise<void>;
  completeGoal: (id: string) => Promise<void>;
  deleteGoal: (id: string) => Promise<void>;
  ensureParticipant: () => Promise<void>;
  signOut: () => Promise<void>;
  getMonthSummary: () => MonthSummary;
  getAllCommitments: () => CommitmentItem[];
  getAllLaunches: () => CommitmentItem[];
  getCategoryTotals: (month: number, year: number) => Record<CategoryId, number>;
  getDashboardData: () => {
    summary: MonthSummary;
    totals: Record<CategoryId, number>;
    allCommitments: CommitmentItem[];
  };
  getNetWorth: () => number;
}

const HOUSE_ID = DEFAULT_HOUSE_ID;

export const useStore = create<AppState>((set, get) => {
  const { month, year } = currentMonthYear();
  const monthKey = (m: number, y: number) => `${y}-${String(m).padStart(2, '0')}`;
  const isNetworkAvailable = () => typeof navigator === 'undefined' || navigator.onLine !== false;

  const applyCachedMonth = (cached: FinanceCacheSnapshot, m: number, y: number) => {
    const data = cached.months[monthKey(m, y)];
    if (!data) return false;
    set({
      invoices: data.invoices ?? [],
      recurringOccurrences: data.recurringOccurrences ?? [],
      incomes: data.incomes ?? [],
      installmentOccurrences: data.installmentOccurrences ?? [],
      oneOffExpenses: data.oneOffExpenses ?? [],
      budgets: data.budgets ?? [],
    });
    return true;
  };

  const applyCachedSnapshot = (cached: FinanceCacheSnapshot, m: number, y: number, offline = true) => {
    set({
      house: cached.house ?? null,
      participants: cached.participants ?? [],
      cards: cached.cards ?? [],
      recurringExpenses: cached.recurringExpenses ?? [],
      installments: cached.installments ?? [],
      recurringIncomes: cached.recurringIncomes ?? [],
      accounts: cached.accounts ?? [],
      goals: cached.goals ?? [],
      isOffline: offline,
      lastSyncedAt: cached.savedAt,
    });
    applyCachedMonth(cached, m, y);
  };

  const persistCache = async (history?: MonthHistory[]) => {
    const state = get();
    const previous = await readFinanceCache(HOUSE_ID);
    const currentKey = monthKey(state.selectedMonth, state.selectedYear);
    const currentMonth: CachedMonthData = {
      invoices: state.invoices,
      recurringOccurrences: state.recurringOccurrences,
      incomes: state.incomes,
      installmentOccurrences: state.installmentOccurrences,
      oneOffExpenses: state.oneOffExpenses,
      budgets: state.budgets,
    };
    const snapshot: FinanceCacheSnapshot = {
      version: 1,
      savedAt: new Date().toISOString(),
      house: state.house,
      participants: state.participants,
      cards: state.cards,
      recurringExpenses: state.recurringExpenses,
      installments: state.installments,
      recurringIncomes: state.recurringIncomes,
      accounts: state.accounts,
      goals: state.goals,
      months: { ...(previous?.months ?? {}), [currentKey]: currentMonth },
      ...(history ? { history } : previous?.history ? { history: previous.history } : {}),
    };
    await writeFinanceCache(HOUSE_ID, snapshot);
    set({ lastSyncedAt: snapshot.savedAt });
  };

  const fetchBudgetsWithSeed = async (month: number, year: number): Promise<Budget[]> => {
    const { data: current, error: currentError } = await supabase.from('budgets').select('*')
      .eq('house_id', HOUSE_ID).eq('month', month).eq('year', year);
    if (currentError) throw new Error(currentError.message);
    if (current && current.length > 0) return current as Budget[];

    const previousDate = new Date(year, month - 2, 1);
    const previousMonth = previousDate.getMonth() + 1;
    const previousYear = previousDate.getFullYear();
    const { data: previous, error: previousError } = await supabase.from('budgets').select('category, amount')
      .eq('house_id', HOUSE_ID).eq('month', previousMonth).eq('year', previousYear);
    if (previousError) throw new Error(previousError.message);
    if (!previous?.length) return [];

    const inherited = previous.map(budget => ({
      house_id: HOUSE_ID,
      category: budget.category,
      month,
      year,
      amount: budget.amount,
    }));
    const { data: seeded, error: seedError } = await supabase.from('budgets')
      .upsert(inherited, { onConflict: 'house_id,category,month,year' })
      .select('*');
    if (seedError) throw new Error(seedError.message);
    return (seeded as Budget[]) ?? [];
  };

  return {
    selectedMonth: month,
    selectedYear: year,
    house: null,
    participants: [],
    cards: [],
    invoices: [],
    recurringExpenses: [],
    recurringOccurrences: [],
    recurringIncomes: [],
    incomes: [],
    installments: [],
    installmentOccurrences: [],
    oneOffExpenses: [],
    accounts: [],
    budgets: [],
    goals: [],
    loading: true,
    error: null,
    isOffline: false,
    lastSyncedAt: null,

    setMonth: (month, year) => {
      set({ selectedMonth: month, selectedYear: year });
      get().fetchMonthData(month, year);
    },

    ensureParticipant: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;
      // Check if this auth user already has a participant record
      const { data: existing } = await supabase
        .from('participants')
        .select('id')
        .eq('auth_user_id', user.id)
        .maybeSingle();
      if (!existing) {
        // Create a participant record so RLS policies allow house data access
        const displayName = (user.user_metadata as any)?.name
          || user.email?.split('@')[0]
          || 'Usuário';
        await supabase.from('participants').insert({
          house_id: HOUSE_ID,
          auth_user_id: user.id,
          name: displayName,
          avatar_emoji: '😊',
        });
      }
    },

    fetchAll: async () => {
      const cached = await readFinanceCache(HOUSE_ID);
      const { selectedMonth: month, selectedYear: year } = get();
      if (cached) applyCachedSnapshot(cached, month, year, !isNetworkAvailable());
      set({ loading: true, error: null });
      if (!isNetworkAvailable()) {
        set({ loading: false, isOffline: true, error: null });
        return;
      }
      try {
        // Guarantee the auth user has a participant record (required for RLS)
        await get().ensureParticipant();

        const [
          { data: house },
          { data: participants },
          { data: cards },
          { data: recurringExpenses },
          { data: installments },
          { data: recurringIncomes },
          { data: accounts },
          { data: goals },
        ] = await Promise.all([
          supabase.from('houses').select('*').eq('id', HOUSE_ID).single(),
          supabase.from('participants').select('*').eq('house_id', HOUSE_ID),
          supabase.from('cards').select('*').eq('house_id', HOUSE_ID).eq('is_active', true).order('created_at'),
          supabase.from('recurring_expenses').select('*').eq('house_id', HOUSE_ID).eq('is_active', true),
          supabase.from('installments').select('*, card:cards(*)').eq('house_id', HOUSE_ID).eq('is_active', true),
          supabase.from('recurring_incomes').select('*').eq('house_id', HOUSE_ID).eq('is_active', true),
          supabase.from('accounts').select('*').eq('house_id', HOUSE_ID).eq('is_active', true).order('created_at'),
          supabase.from('goals').select('*').eq('house_id', HOUSE_ID).eq('is_active', true).eq('is_completed', false).order('created_at'),
        ]);

        set({
          house: house ?? null,
          participants: participants ?? [],
          cards: cards ?? [],
          recurringExpenses: recurringExpenses ?? [],
          installments: (installments as any[]) ?? [],
          recurringIncomes: recurringIncomes ?? [],
          accounts: (accounts as Account[]) ?? [],
          goals: (goals as Goal[]) ?? [],
          isOffline: false,
        });

        await persistCache();
        await get().fetchMonthData(month, year);
      } catch (e: any) {
        if (cached) set({ isOffline: true, error: null });
        else set({ error: e.message ?? 'Não foi possível carregar seus dados.' });
      } finally {
        set({ loading: false });
      }
    },

    fetchMonthData: async (month, year) => {
      const cached = await readFinanceCache(HOUSE_ID);
      if (!isNetworkAvailable()) {
        if (cached && get().selectedMonth === month && get().selectedYear === year) applyCachedMonth(cached, month, year);
        set({ loading: false, isOffline: true, error: null, lastSyncedAt: cached?.savedAt ?? get().lastSyncedAt });
        return;
      }
      set({ loading: true, error: null });
      try {
        await get().generateOccurrences(month, year);

        // Auto-generate income entries from recurring income templates
        const { recurringIncomes } = get();
        await Promise.all(recurringIncomes.filter(r => r.is_active).map(ri => supabase.from('income').upsert({
            house_id: HOUSE_ID,
            month,
            year,
            name: ri.name,
            amount: ri.amount,
            is_recurring: true,
            recurring_income_id: ri.id,
          }, { onConflict: 'house_id,recurring_income_id,month,year', ignoreDuplicates: true })));

        const [
          { data: invoices },
          { data: recurringOccurrences },
          { data: incomes },
          { data: installmentOccurrences },
          { data: oneOffExpenses },
        ] = await Promise.all([
          supabase.from('invoices').select('*, card:cards(*)').eq('house_id', HOUSE_ID).eq('month', month).eq('year', year),
          supabase.from('recurring_occurrences').select('*, recurring_expense:recurring_expenses(*)').eq('house_id', HOUSE_ID).eq('month', month).eq('year', year),
          supabase.from('income').select('*').eq('house_id', HOUSE_ID).eq('month', month).eq('year', year),
          supabase.from('installment_occurrences').select('*, installment:installments(*, card:cards(*))').eq('house_id', HOUSE_ID).eq('month', month).eq('year', year),
          supabase.from('one_off_expenses').select('*').eq('house_id', HOUSE_ID).eq('month', month).eq('year', year),
        ]);
        const budgets = await fetchBudgetsWithSeed(month, year);

        // Guard against stale results if the user navigated to a different month while fetching
        const { selectedMonth, selectedYear } = get();
        if (selectedMonth !== month || selectedYear !== year) return;

        set({
          invoices: (invoices as any[]) ?? [],
          recurringOccurrences: (recurringOccurrences as any[]) ?? [],
          incomes: incomes ?? [],
          installmentOccurrences: (installmentOccurrences as any[]) ?? [],
          oneOffExpenses: (oneOffExpenses as any[]) ?? [],
          budgets: (budgets as Budget[]) ?? [],
          isOffline: false,
        });
        await persistCache();
      } catch (e: any) {
        if (get().selectedMonth !== month || get().selectedYear !== year) return;
        if (cached?.months[monthKey(month, year)]) {
          applyCachedMonth(cached, month, year);
          set({ isOffline: true, error: null, lastSyncedAt: cached.savedAt });
        } else {
          set({ error: e.message ?? 'Não foi possível carregar este mês.' });
        }
      } finally {
        set({ loading: false });
      }
    },

    fetchReportData: async (months = 6) => {
      const { selectedMonth: currentMonth, selectedYear: currentYear } = get();
      const periods = getPeriodMonths(months, currentMonth, currentYear);
      // PostgREST accepts a disjunction of month/year pairs. This keeps the
      // report query bounded to the selected window even when it crosses years.
      const periodFilter = periods
        .map(period => `and(month.eq.${period.m},year.eq.${period.y})`)
        .join(',');
      const cached = await readFinanceCache(HOUSE_ID);

      if (!isNetworkAvailable()) {
        const cachedRows = periods.map(({ m, y }) => cached?.months[monthKey(m, y)] ?? {
          invoices: [], recurringOccurrences: [], incomes: [], installmentOccurrences: [], oneOffExpenses: [], budgets: [],
        });
        const commitments = commitmentItemsFromRows({
          invoices: cachedRows.flatMap(data => data.invoices),
          recurringOccurrences: cachedRows.flatMap(data => data.recurringOccurrences),
          installmentOccurrences: cachedRows.flatMap(data => data.installmentOccurrences),
          oneOffExpenses: cachedRows.flatMap(data => data.oneOffExpenses),
        }).filter(item => isPeriodRow(item, periods));
        return {
          history: historyFromRows(periods, { incomes: cachedRows.flatMap(data => data.incomes), commitments }),
          commitments,
        };
      }

      const [incomeResponse, recurringResponse, installmentResponse, invoiceResponse, oneOffResponse] = await Promise.all([
        supabase.from('income').select('month, year, amount').eq('house_id', HOUSE_ID).or(periodFilter),
        supabase.from('recurring_occurrences').select('*, recurring_expense:recurring_expenses(*)').eq('house_id', HOUSE_ID).or(periodFilter),
        supabase.from('installment_occurrences').select('*, installment:installments(*, card:cards(*))').eq('house_id', HOUSE_ID).or(periodFilter),
        supabase.from('invoices').select('*, card:cards(*)').eq('house_id', HOUSE_ID).or(periodFilter),
        supabase.from('one_off_expenses').select('*').eq('house_id', HOUSE_ID).or(periodFilter),
      ]);
      const responses = [incomeResponse, recurringResponse, installmentResponse, invoiceResponse, oneOffResponse];
      const responseError = responses.find(response => response.error)?.error;
      if (responseError) throw new Error(responseError.message);

      const commitments = commitmentItemsFromRows({
        invoices: invoiceResponse.data ?? [],
        recurringOccurrences: recurringResponse.data ?? [],
        installmentOccurrences: installmentResponse.data ?? [],
        oneOffExpenses: oneOffResponse.data ?? [],
      }).filter(item => isPeriodRow(item, periods));
      const history = historyFromRows(periods, { incomes: incomeResponse.data ?? [], commitments });
      await persistCache(history);
      return { history, commitments };
    },

    fetchMonthHistory: async () => (await get().fetchReportData(6)).history,

    generateOccurrences: async (month, year) => {
      const { recurringExpenses, recurringOccurrences: existingOccurrences } = get();
      if (!recurringExpenses.length) return;

      await Promise.all(recurringExpenses.map(async re => {
        // A recurring expense only starts in its explicitly selected month.
        // Legacy rows without these fields keep the previous behavior until edited.
        if (re.start_month && re.start_year) {
          const isBeforeStart = year < re.start_year
            || (year === re.start_year && month < re.start_month);
          if (isBeforeStart) return;
        }

        const dueDate = re.due_day
          ? `${year}-${String(month).padStart(2, '0')}-${String(Math.min(re.due_day, getDaysInMonth(new Date(year, month - 1)))).padStart(2, '0')}`
          : undefined;

        const { error: insertError } = await supabase.from('recurring_occurrences').upsert({
          recurring_expense_id: re.id,
          house_id: HOUSE_ID,
          month,
          year,
          amount: re.amount,
          due_date: dueDate ?? null,
          status: 'pending',
        }, { onConflict: 'recurring_expense_id,month,year', ignoreDuplicates: true });
        if (insertError) throw new Error(insertError.message);

        // Only update if values actually changed vs what's already in the store.
        // This prevents a Realtime feedback loop: generateOccurrences writes →
        // Realtime fires → fetchMonthData → generateOccurrences writes again → ...
        // Skip amount update for variable expenses (is_fixed === false) so
        // the user-edited value for the month is preserved across refetches.
        const isVariable = re.is_fixed === false;
        const newDueDate = dueDate ?? null;
        const existing = existingOccurrences.find(
          o => o.recurring_expense_id === re.id && o.month === month && o.year === year
        );
        const amountWouldChange = !isVariable && re.amount !== undefined && existing?.amount !== re.amount;
        const dueDateWouldChange = existing ? existing.due_date !== newDueDate : newDueDate !== null;
        if (!amountWouldChange && !dueDateWouldChange) return;

        const pendingUpdate: { amount?: number; due_date: string | null } = { due_date: newDueDate };
        if (!isVariable && re.amount !== undefined) pendingUpdate.amount = re.amount;
        const { error: updateError } = await supabase
          .from('recurring_occurrences')
          .update(pendingUpdate)
          .eq('recurring_expense_id', re.id)
          .eq('house_id', HOUSE_ID)
          .eq('month', month)
          .eq('year', year)
          .eq('status', 'pending');
        if (updateError) throw new Error(updateError.message);
      }));
    },

    addIncome: async (data) => {
      if (!data.name?.trim()) return;
      const amount = Number(data.amount);
      if (!amount || amount <= 0) return;
      const { selectedMonth: month, selectedYear: year } = get();
      await runFinanceCommand({
        kind: 'create_income', description: data.name.trim(), amount,
        date: data.received_at ?? null, month, year, paymentMethod: data.payment_method,
        participantId: data.participant_id,
      });
      await get().fetchMonthData(month, year);
    },

    updateIncome: async (id, patch) => {
      const dbPatch: Record<string, unknown> = {};
      if (patch.name !== undefined) dbPatch.name = patch.name;
      if (patch.amount !== undefined) dbPatch.amount = patch.amount;
      if (patch.date !== undefined) dbPatch.received_at = patch.date;
      const { error } = await supabase.from('income').update(dbPatch).eq('id', id);
      if (error) throw new Error(error.message);
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    deleteIncome: async (id) => {
      const { error } = await supabase.from('income').delete().eq('id', id);
      if (error) throw new Error(error.message);
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    upsertInvoice: async (data) => {
      const { selectedMonth: month, selectedYear: year } = get();
      const { error } = await supabase.from('invoices').upsert({
        house_id: HOUSE_ID,
        month,
        year,
        status: 'open',
        category: normalizeCategory(data.category),
        ...data,
      }, { onConflict: 'card_id,month,year' });
      if (error) throw new Error(error.message);
      await get().fetchMonthData(month, year);
    },

    updateInvoice: async (id, data) => {
      const { error } = await supabase.from('invoices').update({ amount: data.amount }).eq('id', id);
      if (error) throw new Error(error.message);
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    deleteInvoice: async (id) => {
      const { error } = await supabase.from('invoices').delete().eq('id', id);
      if (error) throw new Error(error.message);
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    payInvoice: async (invoiceId) => {
      const { error } = await supabase.from('invoices').update({
        status: 'paid', paid_at: new Date().toISOString(),
      }).eq('id', invoiceId);
      if (error) { set({ error: error.message }); return; }
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    unpayInvoice: async (invoiceId) => {
      const { error } = await supabase.from('invoices').update({
        status: 'open', paid_at: null,
      }).eq('id', invoiceId);
      if (error) { set({ error: error.message }); return; }
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    payOccurrence: async (occurrenceId) => {
      const { error } = await supabase.from('recurring_occurrences').update({
        status: 'paid', paid_at: new Date().toISOString(),
      }).eq('id', occurrenceId);
      if (error) { set({ error: error.message }); return; }
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    unpayOccurrence: async (occurrenceId) => {
      const { error } = await supabase.from('recurring_occurrences').update({
        status: 'pending', paid_at: null,
      }).eq('id', occurrenceId);
      if (error) { set({ error: error.message }); return; }
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    payInstallment: async (occurrenceId) => {
      const { error } = await supabase.from('installment_occurrences').update({
        status: 'paid', paid_at: new Date().toISOString(),
      }).eq('id', occurrenceId);
      if (error) { set({ error: error.message }); return; }
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    unpayInstallment: async (occurrenceId) => {
      const { error } = await supabase.from('installment_occurrences').update({
        status: 'pending', paid_at: null,
      }).eq('id', occurrenceId);
      if (error) { set({ error: error.message }); return; }
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    updateOccurrenceAmount: async (occurrenceId, amount) => {
      const { error } = await supabase.from('recurring_occurrences').update({ amount }).eq('id', occurrenceId);
      if (error) throw new Error(error.message);
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    updateInstallment: async (id, data) => {
      const installment = get().installments.find(item => item.id === id);
      if (!installment) throw new Error('Parcela não encontrada.');
      const amount = Number(data.installment_amount);
      const count = Math.floor(Number(data.installment_count));
      if (!Number.isFinite(amount) || amount <= 0) throw new Error('Informe um valor válido.');
      if (!Number.isInteger(count) || count < 1 || count > 60) throw new Error('Informe entre 1 e 60 parcelas.');

      const { data: paidRows, error: paidError } = await supabase.from('installment_occurrences')
        .select('installment_number').eq('installment_id', id).eq('status', 'paid');
      if (paidError) throw new Error(paidError.message);
      const paidNumbers = new Set((paidRows ?? []).map(row => Number(row.installment_number)));
      const highestPaid = Math.max(0, ...Array.from(paidNumbers));
      if (count < highestPaid) {
        throw new Error(`Não é possível reduzir para ${count} parcelas: a parcela ${highestPaid} já foi paga.`);
      }

      const previousHeader = {
        total_amount: installment.total_amount,
        installment_count: installment.installment_count,
        installment_amount: installment.installment_amount,
      };
      const { error: headerError } = await supabase.from('installments').update({
        total_amount: amount * count,
        installment_count: count,
        installment_amount: amount,
      }).eq('id', id);
      if (headerError) throw new Error(headerError.message);

      const { error: cancelError } = await supabase.from('installment_occurrences')
        .delete()
        .eq('installment_id', id)
        .in('status', ['pending', 'cancelled']);
      if (cancelError) {
        await supabase.from('installments').update(previousHeader).eq('id', id);
        throw new Error(cancelError.message);
      }

      const futureOccurrences = Array.from({ length: count }, (_, index) => {
        const date = new Date(installment.start_year, installment.start_month - 1 + index, 1);
        return {
          house_id: HOUSE_ID,
          installment_id: id,
          installment_number: index + 1,
          month: date.getMonth() + 1,
          year: date.getFullYear(),
          amount,
          status: 'pending' as const,
        };
      }).filter(row => !paidNumbers.has(row.installment_number));

      const { error: occurrenceError } = futureOccurrences.length
        ? await supabase.from('installment_occurrences').insert(futureOccurrences)
        : { error: null };
      if (occurrenceError) throw new Error(occurrenceError.message);
      await get().fetchAll();
    },

    deleteInstallment: async (id) => {
      const { error: headerError } = await supabase.from('installments').update({ is_active: false }).eq('id', id);
      if (headerError) throw new Error(headerError.message);
      const { error: occurrenceError } = await supabase.from('installment_occurrences')
        .update({ status: 'cancelled' })
        .eq('installment_id', id)
        .neq('status', 'cancelled');
      if (occurrenceError) {
        await supabase.from('installments').update({ is_active: true }).eq('id', id);
        throw new Error(occurrenceError.message);
      }
      await get().fetchAll();
    },

    addCard: async (data) => {
      if (!data.name?.trim()) return;
      const { error } = await supabase.from('cards').insert({
        house_id: HOUSE_ID,
        is_active: true,
        color: '#7c3aed',
        holder: 'Conjunto',
        ...data,
      });
      if (error) throw new Error(error.message);
      await get().fetchAll();
    },

    updateCard: async (id, data) => {
      const patch = {
        ...(data.name !== undefined ? { name: data.name.trim() } : {}),
        ...(data.last_digits !== undefined ? { last_digits: data.last_digits?.trim() || null } : {}),
        ...(data.due_day !== undefined ? { due_day: data.due_day || null } : {}),
      };
      if (!Object.keys(patch).length) return;
      const { error } = await supabase.from('cards').update(patch).eq('id', id).eq('house_id', HOUSE_ID);
      if (error) throw new Error(error.message);
      await get().fetchAll();
    },

    deleteCard: async (id) => {
      const { error } = await supabase.from('cards').update({ is_active: false }).eq('id', id);
      if (error) throw new Error(error.message);
      await get().fetchAll();
    },

    addRecurring: async (data) => {
      if (!data.name?.trim()) return;
      const amount = Number(data.amount);
      if (!amount || amount <= 0) return;
      const { selectedMonth: month, selectedYear: year } = get();
      await runFinanceCommand({
        kind: 'create_fixed_expense', description: data.name.trim(), amount,
        month: Number(data.start_month ?? month), year: Number(data.start_year ?? year),
        category: normalizeCategory(data.category), dueDay: data.due_day ?? null,
        participantId: data.participant_id ?? null,
      });
      await get().fetchAll();
    },

    updateRecurring: async (id, data) => {
      const { error } = await supabase.from('recurring_expenses').update(data).eq('id', id);
      if (error) throw new Error(error.message);
      // Update pending occurrences amount so existing month rows reflect the new value
      if (data.amount !== undefined) {
        await supabase
          .from('recurring_occurrences')
          .update({ amount: data.amount })
          .eq('recurring_expense_id', id)
          .eq('status', 'pending');
      }
      await get().fetchAll();
    },

    deleteRecurring: async (id) => {
      const { error } = await supabase.from('recurring_expenses').update({ is_active: false }).eq('id', id);
      if (error) throw new Error(error.message);
      await get().fetchAll();
    },

    deleteRecurringOccurrence: async (id) => {
      const { error } = await supabase.from('recurring_occurrences').delete().eq('id', id);
      if (error) throw new Error(error.message);
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    addRecurringIncome: async (data) => {
      if (!data.name?.trim()) return;
      const amount = Number(data.amount);
      if (!amount || amount <= 0) return;
      const { error } = await supabase.from('recurring_incomes').insert({
        house_id: HOUSE_ID,
        is_active: true,
        ...data,
      });
      if (error) throw new Error(error.message);
      await get().fetchAll();
    },

    deleteRecurringIncome: async (id) => {
      const { error } = await supabase.from('recurring_incomes').update({ is_active: false }).eq('id', id);
      if (error) throw new Error(error.message);
      await get().fetchAll();
    },

    addOneOff: async (data) => {
      if (!data.name?.trim()) return;
      const amount = Number(data.amount);
      if (!amount || amount <= 0) return;
      const { selectedMonth: month, selectedYear: year } = get();
      const created = await runFinanceCommand({
        kind: 'create_expense', description: data.name.trim(), amount,
        date: data.due_date ?? null, month, year, category: normalizeCategory(data.category),
        paymentMethod: data.payment_method, participantId: data.participant_id, paid: false,
      });
      await get().fetchMonthData(month, year);
      return created.id;
    },

    updateOneOff: async (id, patch) => {
      const dbPatch: Record<string, unknown> = {};
      if (patch.name !== undefined) dbPatch.name = patch.name;
      if (patch.amount !== undefined) dbPatch.amount = patch.amount;
      if (patch.date !== undefined) dbPatch.due_date = patch.date;
      if (patch.category !== undefined) dbPatch.category = patch.category;
      const { error } = await supabase.from('one_off_expenses').update(dbPatch).eq('id', id);
      if (error) throw new Error(error.message);
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    deleteOneOff: async (id) => {
      const { error } = await supabase.from('one_off_expenses').delete().eq('id', id);
      if (error) throw new Error(error.message);
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    payOneOff: async (id) => {
      const { error } = await supabase.from('one_off_expenses').update({
        status: 'paid', paid_at: new Date().toISOString(),
      }).eq('id', id);
      if (error) { set({ error: error.message }); return; }
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    unpayOneOff: async (id) => {
      const { error } = await supabase.from('one_off_expenses').update({
        status: 'pending', paid_at: null,
      }).eq('id', id);
      if (error) { set({ error: error.message }); return; }
      const { selectedMonth: m, selectedYear: y } = get();
      await get().fetchMonthData(m, y);
    },

    addInstallment: async ({ card_id, name, amount, total_installments, start_month, start_year, category = 'outros' }) => {
      if (!name.trim() || !card_id || amount <= 0 || total_installments < 1) return;
      const { selectedMonth, selectedYear } = get();
      const startMonth = start_month ?? selectedMonth;
      const startYear = start_year ?? selectedYear;
      const { data: inst, error } = await supabase.from('installments').insert({
        house_id: HOUSE_ID,
        card_id,
        name: name.trim(),
        total_amount: amount * total_installments,
        installment_count: total_installments,
        installment_amount: amount,
        category,
        start_month: startMonth,
        start_year: startYear,
        is_active: true,
      }).select().single();
      if (error || !inst) { set({ error: error?.message ?? 'Erro ao criar parcela' }); return; }

      // Build all occurrences in one batch to avoid partial creation
      const occurrences = Array.from({ length: total_installments }, (_, i) => {
        let m = startMonth + i, y = startYear;
        while (m > 12) { m -= 12; y++; }
        return {
          house_id: HOUSE_ID,
          installment_id: inst.id,
          installment_number: i + 1,
          month: m, year: y,
          amount, status: 'pending' as const,
        };
      });

      const { error: occErr } = await supabase.from('installment_occurrences').insert(occurrences);
      if (occErr) {
        // Rollback: deactivate the installment header so it doesn't show as orphaned
        await supabase.from('installments').update({ is_active: false }).eq('id', inst.id);
        set({ error: occErr.message ?? 'Erro ao criar parcelas' });
        return;
      }
      await get().fetchAll();
    },

    fetchAccounts: async () => {
      const { data, error } = await supabase
        .from('accounts').select('*').eq('house_id', HOUSE_ID).eq('is_active', true).order('created_at');
      if (error) throw new Error(error.message);
      set({ accounts: (data as Account[]) ?? [] });
    },

    addAccount: async (data) => {
      if (!data.name?.trim()) return;
      const balance = Number(data.balance ?? 0);
      if (!Number.isFinite(balance)) return;
      const { error } = await supabase.from('accounts').insert({
        house_id: HOUSE_ID, name: data.name.trim(), type: data.type ?? 'corrente',
        balance, color: data.color ?? '#4F46E5', is_active: true,
      });
      if (error) throw new Error(error.message);
      await get().fetchAccounts();
    },

    updateAccountBalance: async (id, balance) => {
      if (!Number.isFinite(balance)) return;
      const { error } = await supabase.from('accounts').update({ balance }).eq('id', id);
      if (error) throw new Error(error.message);
      await get().fetchAccounts();
    },

    deleteAccount: async (id) => {
      const { error } = await supabase.from('accounts').update({ is_active: false }).eq('id', id);
      if (error) throw new Error(error.message);
      await get().fetchAccounts();
    },

    fetchBudgets: async (month, year) => {
      set({ budgets: await fetchBudgetsWithSeed(month, year) });
    },

    saveBudget: async (category, amount, month, year) => {
      if (!Number.isFinite(amount) || amount < 0) return;
      const { error } = await supabase.from('budgets').upsert(
        { house_id: HOUSE_ID, category, amount, month, year },
        { onConflict: 'house_id,category,month,year' },
      );
      if (error) throw new Error(error.message);
      await get().fetchBudgets(month, year);
    },

    deleteBudget: async (id) => {
      const { error } = await supabase.from('budgets').delete().eq('id', id);
      if (error) throw new Error(error.message);
      await get().fetchBudgets(get().selectedMonth, get().selectedYear);
    },

    fetchGoals: async () => {
      const { data, error } = await supabase.from('goals').select('*')
        .eq('house_id', HOUSE_ID).eq('is_active', true).eq('is_completed', false).order('created_at');
      if (error) throw new Error(error.message);
      set({ goals: (data as Goal[]) ?? [] });
    },

    addGoal: async (data) => {
      const target = Number(data.target_amount);
      if (!data.name?.trim() || !Number.isFinite(target) || target <= 0) return;
      const { error } = await supabase.from('goals').insert({
        house_id: HOUSE_ID, name: data.name.trim(), target_amount: target,
        current_amount: Number(data.current_amount ?? 0), target_date: data.target_date ?? null,
        emoji: data.emoji ?? '🎯', is_completed: false, is_active: true,
      });
      if (error) throw new Error(error.message);
      await get().fetchGoals();
    },

    updateGoalProgress: async (id, amount) => {
      if (!Number.isFinite(amount) || amount < 0) return;
      const { error } = await supabase.from('goals').update({ current_amount: amount }).eq('id', id);
      if (error) throw new Error(error.message);
      await get().fetchGoals();
    },

    completeGoal: async (id) => {
      const { error } = await supabase.from('goals').update({ is_completed: true }).eq('id', id);
      if (error) throw new Error(error.message);
      await get().fetchGoals();
    },

    deleteGoal: async (id) => {
      const { error } = await supabase.from('goals').update({ is_active: false }).eq('id', id);
      if (error) throw new Error(error.message);
      await get().fetchGoals();
    },

    signOut: async () => {
      // Sign out from all devices, not just the current session
      await supabase.auth.signOut({ scope: 'global' });
      await clearFinanceCache(HOUSE_ID);
      set({
        house: null,
        participants: [],
        cards: [],
        invoices: [],
        recurringExpenses: [],
        recurringOccurrences: [],
        recurringIncomes: [],
        incomes: [],
        installments: [],
        installmentOccurrences: [],
        oneOffExpenses: [],
        accounts: [],
        budgets: [],
        goals: [],
        isOffline: false,
        lastSyncedAt: null,
      });
    },

    getMonthSummary: () => {
      return get().getDashboardData().summary;
    },

    getAllCommitments: () => {
      const { invoices, recurringOccurrences, installmentOccurrences, oneOffExpenses } = get();
      return commitmentItemsFromRows({ invoices, recurringOccurrences, installmentOccurrences, oneOffExpenses });
    },

    getAllLaunches: () => {
      const { incomes, invoices, recurringOccurrences, installmentOccurrences, oneOffExpenses } = get();
      return launchItemsFromRows({ incomes, invoices, recurringOccurrences, installmentOccurrences, oneOffExpenses });
    },

    getCategoryTotals: (month, year) => {
      const totals = Object.fromEntries(
        ['alimentacao', 'moradia', 'transporte', 'saude', 'lazer', 'educacao', 'vestuario', 'assinaturas', 'investimentos', 'pessoal', 'presentes', 'outros']
          .map(category => [category, 0]),
      ) as Record<CategoryId, number>;
      const state = get();
      // The store keeps only the selected month's rows. Returning an empty
      // result for another month is safer than silently returning the wrong
      // month's totals; period reports use fetchReportData for wider windows.
      if (month !== state.selectedMonth || year !== state.selectedYear) return totals;
      Object.assign(totals, categoryTotalsForItems(state.getAllCommitments().filter(item => item.month === month && item.year === year)));
      return totals;
    },

    getDashboardData: () => {
      const { invoices, recurringOccurrences, installmentOccurrences, oneOffExpenses, incomes } = get();
      const allCommitments = commitmentItemsFromRows({ invoices, recurringOccurrences, installmentOccurrences, oneOffExpenses });
      const totals = Object.fromEntries(
        ['alimentacao', 'moradia', 'transporte', 'saude', 'lazer', 'educacao', 'vestuario', 'assinaturas', 'investimentos', 'pessoal', 'presentes', 'outros']
          .map(category => [category, 0]),
      ) as Record<CategoryId, number>;
      Object.assign(totals, categoryTotalsForItems(allCommitments.filter(item => item.month === get().selectedMonth && item.year === get().selectedYear)));

      const totalIncome = incomes.reduce((sum, income) => sum + Number(income.amount ?? 0), 0);
      const totalCommitments = allCommitments
        .filter(item => item.month === get().selectedMonth && item.year === get().selectedYear)
        .reduce((sum, item) => sum + Number(item.amount || 0), 0);
      const upcomingItems = allCommitments
        .filter(item => (item.status === 'pending' || item.status === 'overdue') && item.month === get().selectedMonth && item.year === get().selectedYear)
        .sort((a, b) => {
          if (!a.dueDate) return 1;
          if (!b.dueDate) return -1;
          return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
        })
        .slice(0, 5);

      return {
        totals,
        allCommitments,
        summary: {
          totalIncome,
          totalCommitments,
          freeBalance: totalIncome - totalCommitments,
          committedPercent: totalIncome > 0 ? Math.min(Math.round((totalCommitments / totalIncome) * 100), 100) : 0,
          upcomingItems,
        },
      };
    },

    getNetWorth: () => get().accounts
      .filter(account => account.is_active)
      .reduce((sum, account) => sum + Number(account.balance || 0), 0),
  };
});
